import { useEffect, useRef } from "react";
import { AppsFlyer, AFConstants } from "appsflyer-capacitor-plugin";
import { AppTrackingTransparency } from "capacitor-plugin-app-tracking-transparency";
import { Capacitor } from "@capacitor/core";
import { useMixpanel } from "../MixpanelContext/MixpanelProvider";
import { useSupabaseAuth } from "../AuthContext/AuthContext";

const APPS_FLYER_DEV_KEY = "Hx39EusqYxTttcoqRcD6c3";
const APP_ID = "6502530085";

const options = {
  devKey: APPS_FLYER_DEV_KEY,
  appID: APP_ID,
  isDebug: false,
  waitForATTUserAuthorization: 60, // seconds to wait for ATT response
};

export const AppsFlyerProvider = () => {
  const Mixpanel = useMixpanel();
  const attributionTracked = useRef(false);
  const { useAuthStore } = useSupabaseAuth();

  useEffect(() => {
    const initAppsFlyer = async () => {
      try {
        // Check if we're on iOS platform
        if (Capacitor.getPlatform() === "ios") {
          // Check if ATT plugin is available
          if (Capacitor.isPluginAvailable("AppTrackingTransparency")) {
            console.log("Checking ATT permission status...");

            // Get current ATT status
            const statusResult: any = await AppTrackingTransparency.getStatus();
            console.log("Current ATT status:", statusResult.status);

            // If status is not determined, request permission
            if (statusResult.status === "notDetermined") {
              console.log("Requesting ATT permission...");
              const requestResult: any =
                await AppTrackingTransparency.requestPermission();
              console.log("ATT request result:", requestResult.status);

              // Initialize AppsFlyer only if authorized
              if (requestResult.status === "authorized") {
                console.log(
                  "ATT permission granted, initializing AppsFlyer...",
                );
                await initAppsFlyerSDK();
              } else {
                console.log(
                  "ATT permission not granted. AppsFlyer will initialize with limited tracking.",
                );
                // You can still initialize AppsFlyer but tracking will be limited
                await initAppsFlyerSDK();
              }
            } else if (statusResult.status === "authorized") {
              // Already authorized, proceed with AppsFlyer initialization
              console.log("ATT already authorized, initializing AppsFlyer...");
              await initAppsFlyerSDK();
            } else {
              // ATT denied or restricted, but still initialize AppsFlyer
              console.log(
                "ATT not authorized, but initializing AppsFlyer with limited tracking...",
              );
              await initAppsFlyerSDK();
            }
          } else {
            // ATT plugin not available, proceed with AppsFlyer initialization
            console.log("ATT plugin not available, initializing AppsFlyer...");
            await initAppsFlyerSDK();
          }
        } else {
          // Not iOS, directly initialize AppsFlyer
          console.log("Not iOS platform, initializing AppsFlyer...");
          await initAppsFlyerSDK();
        }
      } catch (error) {
        console.error(
          "Error during ATT check or AppsFlyer initialization:",
          error,
        );
        // Fallback: try to initialize AppsFlyer anyway
        await initAppsFlyerSDK();
      }
    };

    const initAppsFlyerSDK = async () => {
      try {
        const distinctId = useAuthStore.getState().distinctId;
    
        if (distinctId) {
          try {
            await AppsFlyer.setCustomerUserId(distinctId as any);
            console.log(
              `✅ AppsFlyer Customer User ID set to distinctId from Mixpanel: ${distinctId}`,
            );
            console.log(
              "🔗 Install events will now be linked to this user in Mixpanel",
            );
          } catch (error) {
            console.error(
              "❌ Failed to set AppsFlyer Customer User ID:",
              error,
            );
          }
        } else {
          console.log(
            "ℹ️ No distinctId available from Mixpanel yet, will be set when user authenticates",
          );
        }
        await AppsFlyer.initSDK(options);
        AppsFlyer.addListener(AFConstants.CONVERSION_CALLBACK, (res: any) => {
          console.log("Attribution Source result:", res);
          console.log("Attribution Source:", res.data?.media_source);

          // Only track attribution once to prevent duplicate events
          if (res.data && !attributionTracked.current) {
            attributionTracked.current = true;
            const attributionEventData = {
              af_media_source: res.data.media_source || "organic",
              af_campaign: res.data.campaign,
              af_channel: res.data.af_channel,
              af_adset: res.data.af_adset,
              af_ad: res.data.af_ad,
              install_time: res.data.install_time,
              af_status: res.data.af_status,
              click_time: res.data.click_time,
            };
            Mixpanel.register(attributionEventData);

            // Track attribution event with Mixpanel
            Mixpanel.trackEvent("Install Attribution", {
              name: "AppsFlyer Attribution Data",
              ...attributionEventData,
            });

            console.log(
              "Attribution data registered as super properties and event tracked:",
              attributionEventData,
            );
          } else if (attributionTracked.current) {
            console.log(
              "Attribution already tracked, skipping duplicate event",
            );
          }
        });
        console.log("AppsFlyer initialized successfully");
      } catch (err) {
        console.error("AppsFlyer init failed:", err);
      }
    };

    initAppsFlyer();
  }, []);

  return null;
};
